// Markdown rendering (GitHub-flavored via marked) and HTML sanitizing.
import { marked, Marked, type TokensList } from 'marked';
import DOMPurify from 'dompurify';
import { changedSections, resolveRepoPath, type LineSpan } from './preview';
import { httpUrl } from './url';

/** Task list boxes as glyphs: the sanitizer removes `<input>`. */
function taskBox(checked: boolean): string {
  return `<span class="md-task" role="img" aria-label="${checked ? 'done' : 'not done'}">${checked ? '☑' : '☐'}</span> `;
}

marked.use({ gfm: true, breaks: true, renderer: { checkbox: ({ checked }) => taskBox(checked) } });

/**
 * User content may not restyle the application: `style`, `id` and the legacy
 * `background` attribute are dropped, and `class` keeps only the classes the
 * renderer itself adds (see `KEPT_CLASS`). It may not supply the renderer's
 * own markers either: every `data-*` attribute is dropped, so the ones the
 * hook and the document renderer add are the only ones in the output.
 */
const CONFIG = {
  USE_PROFILES: { html: true },
  ADD_ATTR: ['target'],
  ALLOW_DATA_ATTR: false,
  FORBID_TAGS: ['style', 'form', 'input'],
  FORBID_ATTR: ['style', 'id', 'background'],
};

/**
 * Classes kept on sanitized markup: task boxes and fenced-code languages,
 * which the Markdown renderer writes before sanitizing. The table classes
 * (`frameTables`) and the document renderer's section markers
 * (renderMarkdownDocument) are added after it.
 */
const KEPT_CLASS = /^(md-task|language-[\w+#.-]+)$/;

/**
 * Hosts whose images load directly: GitHub's own (uploads, avatars, the camo
 * proxy GitHub's rendered HTML points at) and the configured GitHub web host.
 * An image anywhere else would tell its host each reviewer's address and
 * viewing time, so it shows as a "Load image" button instead.
 */
const trustedHosts = new Set(['github.com']);
const trustedSuffixes = new Set(['.github.com', '.githubusercontent.com', '.githubassets.com']);

/** Trust images from the GitHub web host changelist is configured for (GitHub Enterprise Server). */
export function trustImageHost(webUrl: string | null | undefined) {
  if (!webUrl) return;
  try {
    const host = new URL(webUrl).hostname.toLowerCase();
    if (!host) return;
    trustedHosts.add(host);
    trustedSuffixes.add(`.${host}`);
  } catch {
    /* not a URL: nothing to trust */
  }
}

/** The host of an absolute http(s) URL that is not trusted for images; null otherwise. */
export function untrustedImageHost(src: string): string | null {
  // WHATWG normalization also treats backslashes as URL separators. Resolve
  // network-path references before classifying the host; ordinary repository
  // paths remain relative so the document renderer can load them itself.
  const normalized = src.replace(/[\t\n\r]/g, '').replace(/^[\x00-\x20]+|[\x00-\x20]+$/g, '').replace(/\\/g, '/');
  const url = httpUrl(normalized.startsWith('//') ? 'https:' + normalized : normalized);
  if (!url) return null;
  const host = new URL(url).hostname.toLowerCase();
  if (trustedHosts.has(host)) return null;
  for (const suffix of trustedSuffixes) if (host.endsWith(suffix)) return null;
  return host;
}

const MEDIA = new Set(['IMG', 'VIDEO', 'AUDIO', 'SOURCE', 'TRACK', 'PICTURE']);

let hooked = false;
function hook() {
  if (hooked) return;
  hooked = true;
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    const el = node as Element;
    if (typeof el.getAttribute !== 'function') return;
    const cls = el.getAttribute('class');
    if (cls !== null) {
      const kept = cls.split(/\s+/).filter((c) => KEPT_CLASS.test(c));
      if (kept.length) el.setAttribute('class', kept.join(' '));
      else el.removeAttribute('class');
    }
    if (el.tagName === 'A' && httpUrl(el.getAttribute('href'))) {
      el.setAttribute('target', '_blank');
      el.setAttribute('rel', 'noreferrer noopener');
    }
    if (MEDIA.has(el.tagName)) {
      for (const attr of ['srcset', 'poster']) {
        const v = el.getAttribute(attr);
        if (v !== null && (attr === 'srcset' || untrustedImageHost(v))) el.removeAttribute(attr);
      }
      const src = el.getAttribute('src');
      const host = src === null ? null : untrustedImageHost(src);
      if (src !== null && host) {
        el.removeAttribute('src');
        if (el.tagName === 'IMG') {
          el.setAttribute('data-external-src', new URL(src, location.href).href);
          el.setAttribute('data-external-host', host);
        }
      }
      if (el.tagName === 'IMG') el.setAttribute('referrerpolicy', 'no-referrer');
    }
  });
}

/** An external image's stand-in: a button that loads it on request. */
function deferImages(root: DocumentFragment) {
  for (const img of root.querySelectorAll<HTMLImageElement>('img[data-external-src]')) {
    const host = img.getAttribute('data-external-host') ?? '';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ext-img';
    btn.setAttribute('data-src', img.getAttribute('data-external-src') ?? '');
    const alt = img.getAttribute('alt') ?? '';
    if (alt) btn.setAttribute('data-alt', alt);
    btn.title = `Loading it shows ${host} your IP address`;
    btn.textContent = `Load image from ${host}${alt ? ` · ${alt}` : ''}`;
    img.replaceWith(btn);
  }
}

/** The most text a table cell, link or code span may hold and still be kept on one line. */
export const TABLE_ATOM_MAX = 32;
/** A table cell with more text than this is prose: its column keeps a readable width. */
export const TABLE_PROSE_MIN = 48;

/** `md-atom` for text short enough to stay on one line, `md-break` for a longer single word; nothing for longer text of several words. */
function wrapClass(text: string): 'md-atom' | 'md-break' | null {
  const t = text.trim();
  if (!t) return null;
  if (t.length <= TABLE_ATOM_MAX) return 'md-atom';
  return /\s/.test(t) ? null : 'md-break';
}

/**
 * Tables, ready for the stylesheet: each outermost `<table>` sits alone in a
 * `div.md-table`, the box that scrolls sideways when the table is wider
 * than its container (`watchTableFrames` names it and makes it focusable
 * while it does). A cell, link or code span of at most `TABLE_ATOM_MAX`
 * characters is marked `md-atom` (never wrapped); a longer one that is a
 * single word (a URL, a hash) is `md-break` (it may break anywhere). A cell
 * of prose (over `TABLE_PROSE_MIN` characters) or one holding an `md-break`
 * is `md-wide`. A column's alignment stays where Markdown puts it, in each
 * cell's `align`.
 */
function frameTables(root: DocumentFragment) {
  for (const cell of root.querySelectorAll('th, td')) {
    for (const el of cell.querySelectorAll('a, code')) {
      const cls = el.closest('pre') ? null : wrapClass(el.textContent ?? '');
      if (cls) el.classList.add(cls);
    }
    const text = cell.textContent ?? '';
    const cls = wrapClass(text);
    if (cls) cell.classList.add(cls);
    if (text.trim().length > TABLE_PROSE_MIN || cell.querySelector('.md-break')) cell.classList.add('md-wide');
  }
  for (const table of root.querySelectorAll('table')) {
    if (table.parentElement?.closest('table')) continue;
    const frame = document.createElement('div');
    frame.className = 'md-table';
    table.replaceWith(frame);
    frame.append(table);
  }
}

/**
 * GitHub's task list boxes (`<input type="checkbox">`, which the sanitizer
 * removes) as the glyphs the Markdown renderer writes. Parsed in a
 * `<template>`, whose document is inert, and sanitized afterwards.
 */
function taskBoxes(html: string): string {
  if (!/<input\b/i.test(html)) return html;
  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  for (const box of tpl.content.querySelectorAll<HTMLInputElement>('input[type="checkbox" i]')) {
    const span = tpl.content.ownerDocument.createElement('span');
    span.className = 'md-task';
    span.setAttribute('role', 'img');
    span.setAttribute('aria-label', box.hasAttribute('checked') ? 'done' : 'not done');
    span.textContent = box.hasAttribute('checked') ? '☑' : '☐';
    box.replaceWith(span);
  }
  return tpl.innerHTML;
}

/**
 * Click handler for a container of sanitized HTML: a "Load image" button is
 * replaced by its image. Returns whether the event was handled.
 */
export function loadExternalImage(e: Event): boolean {
  const btn = (e.target as Element | null)?.closest?.('button.ext-img');
  if (!btn) return false;
  const src = httpUrl(btn.getAttribute('data-src'));
  if (!src) return false;
  const img = document.createElement('img');
  img.referrerPolicy = 'no-referrer';
  img.src = src;
  img.alt = btn.getAttribute('data-alt') ?? '';
  btn.replaceWith(img);
  e.preventDefault();
  return true;
}

/**
 * User HTML made safe to show: sanitized, external images replaced by their
 * "Load image" buttons, task boxes as glyphs and tables framed for the
 * stylesheet (`frameTables`).
 */
export function sanitize(html: string): string {
  hook();
  const clean = DOMPurify.sanitize(taskBoxes(html), CONFIG) as unknown as string;
  if (!clean.includes('data-external-src') && !/<table\b/i.test(clean)) return clean;
  const tpl = document.createElement('template');
  tpl.innerHTML = clean;
  deferImages(tpl.content);
  frameTables(tpl.content);
  return tpl.innerHTML;
}

export function renderMarkdown(md: string): string {
  return sanitize(marked.parse(md ?? '', { async: false }) as string);
}

/**
 * GitHub's rendering of a review comment, cut at its suggested-change
 * blocks (top-level `div.js-suggested-changes-blob`): the HTML before,
 * between and after them, one more segment than there are blocks. The
 * blocks are left out (their code comes from the Markdown, see
 * `splitSuggestions`). Null when there is no block, or when one sits
 * inside other markup, where cutting would split that markup.
 *
 * The segments are NOT sanitized: render each through `bodyHtml` (the
 * `Html` component). They are cut first because sanitizing drops the class
 * that marks a block. The HTML is parsed into a `<template>`, and every
 * node stays in the template's own inert document, which runs no scripts
 * and loads nothing. An untrusted node must never be created by, or moved
 * (adopted) into, the page's `document` before sanitizing: adoption alone
 * starts image fetches and fires `onerror`, even for a detached element.
 */
export function splitSuggestionHtml(html: string): string[] | null {
  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  const blocks = tpl.content.querySelectorAll('.js-suggested-changes-blob');
  if (!blocks.length || [...blocks].some((b) => b.parentNode !== tpl.content)) return null;
  const inert = tpl.content.ownerDocument;
  const segments: string[] = [];
  let seg = inert.createElement('div');
  for (const node of [...tpl.content.childNodes]) {
    if (node.nodeType === 1 /* element */ && (node as Element).classList.contains('js-suggested-changes-blob')) {
      segments.push(seg.innerHTML);
      seg = inert.createElement('div');
    } else seg.append(node);
  }
  segments.push(seg.innerHTML);
  return segments;
}

/** Prefer GitHub's rendered HTML; fall back to client-side rendering. */
export function bodyHtml(html: string | null | undefined, md: string | null | undefined): string {
  if (html && html.trim()) return sanitize(html);
  return renderMarkdown(md ?? '');
}

// ---------------------------------------------------------------- documents

/** Markdown files are rendered as GitHub renders them: line breaks are not `<br>`. */
const doc = new Marked({ gfm: true, breaks: false });
doc.use({ renderer: { checkbox: ({ checked }) => taskBox(checked) } });

export interface DocSection extends LineSpan {
  html: string;
}

/** A Markdown document's top-level blocks with their source lines, rendered (not yet sanitized). */
export function markdownSections(md: string): DocSection[] {
  const src = (md ?? '').replace(/\r\n?/g, '\n');
  const tokens = doc.lexer(src);
  const out: DocSection[] = [];
  let line = 1;
  for (const tok of tokens) {
    const raw = tok.raw ?? '';
    const lead = raw.length - raw.replace(/^\n+/, '').length;
    const body = raw.replace(/^\n+/, '').replace(/\n+$/, '');
    if (tok.type !== 'space' && tok.type !== 'def' && body) {
      const start = line + lead;
      const end = start + (body.match(/\n/g)?.length ?? 0);
      const list = Object.assign([tok], { links: tokens.links }) as unknown as TokensList;
      out.push({ start, end, html: doc.parser(list) });
    }
    line += raw.match(/\n/g)?.length ?? 0;
  }
  return out;
}

export interface DocOptions {
  /** The file's repository path: relative links and images resolve against it. */
  path: string;
  /** Changed source lines (`changedLines`): the sections they touch are marked `md-changed`. */
  changed?: Set<number> | null;
  /** URL a repository link opens (the file at the shown commit on GitHub). */
  href: (target: { path: string; hash: string }) => string;
}

export interface RenderedDoc {
  html: string;
  sections: number;
  changed: number;
}

/**
 * Render a Markdown file: GitHub-flavored, sanitized with the comment
 * sanitizer, top-level blocks wrapped (after sanitizing) in
 * `div.md-sec[data-lines="a-b"]`, changed ones also `.md-changed`.
 * Repository-relative links point at `href`; repository-relative images lose
 * their `src` and carry `data-repo-path` for the caller to load (the page
 * never requests a relative URL from its own origin).
 */
export function renderMarkdownDocument(md: string, opts: DocOptions): RenderedDoc {
  const sections = markdownSections(md);
  const marks = opts.changed ? changedSections(sections, opts.changed) : sections.map(() => false);
  // Each section is sanitized on its own and wrapped afterwards, so its
  // markers can only come from here.
  const html = sections
    .map((s, i) => `<div class="md-sec${marks[i] ? ' md-changed' : ''}" data-lines="${s.start}-${s.end}">${sanitize(s.html)}</div>`)
    .join('\n');
  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  for (const a of tpl.content.querySelectorAll<HTMLAnchorElement>('a[href]')) {
    const target = resolveRepoPath(opts.path, a.getAttribute('href') ?? '');
    if (!target) continue;
    a.setAttribute('href', opts.href(target));
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noreferrer noopener');
  }
  for (const el of tpl.content.querySelectorAll('img, source')) {
    el.removeAttribute('srcset');
    const src = el.getAttribute('src');
    if (src === null) continue;
    const target = resolveRepoPath(opts.path, src);
    if (!target || target.path === opts.path) continue;
    el.removeAttribute('src');
    el.setAttribute('data-repo-path', target.path);
  }
  return { html: tpl.innerHTML, sections: sections.length, changed: marks.filter(Boolean).length };
}
